import { FC, Fragment, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';
import {
  DataRoomAccessStatus,
  DataRoomActivityWindow,
  DEFAULT_DATA_ROOM_ACTIVITY_WINDOW,
  VisitorProfile,
} from 'shared';
import { twJoin } from 'tailwind-merge';

import { ChevronLeftIcon } from '../../../../assets/icons/index';
import activeDataRoomsIcon from '../../../../assets/images/data-room-activity/active-data-rooms.svg';
import activityTimelineIcon from '../../../../assets/images/data-room-activity/activity-timeline.svg';
import roomTotalDownloadsIcon from '../../../../assets/images/data-room-activity/room-total-downloads.svg';
import roomTotalViewsIcon from '../../../../assets/images/data-room-activity/room-total-views.svg';
import totalViewingTimeIcon from '../../../../assets/images/data-room-activity/total-viewing-time.svg';
import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import { AnimatedHeight, StaggerFadeIn, StaggerItem } from '../../../../components/motion/index';
import { useLocale } from '../../../../hooks/index';
import { StringKey } from '../../../../lang/index';
import { PageRoute } from '../../../../types/page.types';
import { AccessAvatar } from '../../workspaces/components/access/access-avatar';
import { LastActive } from '../../workspaces/components/last-active';
import { ActivityLoadError } from '../components/activity-load-error';
import { ActivityVisitorPlaceholder } from '../components/activity-loading-placeholders';
import {
  durationParts,
  MetricCard,
  MetricIcon,
  MetricPanel,
} from '../components/activity-metric-cards';
import { ActivityTimeline } from '../components/activity-timeline';
import { ActivityWindowToggle } from '../components/activity-window-toggle';
import { FilterSelect } from '../components/filter-select';
import { VISITOR_TYPE_LABEL } from '../components/visitor-type-label';
import { useActivityVisitor } from '../hooks/use-activity-visitor.hook';
import { ActivityOutletContext } from '../types/activity-outlet-context.types';

const ACCESS_STATUS: Record<DataRoomAccessStatus, { className: string; label: StringKey }> = {
  [DataRoomAccessStatus.ACTIVE]: { className: 'text-forest-600', label: 'ACTIVE' },
  [DataRoomAccessStatus.PENDING]: { className: 'text-sunbeam-700', label: 'PENDING' },
  [DataRoomAccessStatus.REVOKED]: { className: 'text-fireside-600', label: 'REVOKED' },
};

const VisitorHeader: FC<{
  dataRoomId: string;
  onScope: (dataRoomId: string) => void;
  profile: VisitorProfile;
}> = ({ dataRoomId, onScope, profile }) => {
  const { messagesLocale } = useLocale();
  const { identity } = profile;
  /**
   * Who this is, in the order an Admin reads it: what kind of visitor, where they are from, how to
   * reach them. The type is the same word the tables they came from use, and the count of rooms
   * they hold a grant in is the one fact the header can state that no row on those tables can.
   */
  const details = [
    identity.actorType && messagesLocale[VISITOR_TYPE_LABEL[identity.actorType]],
    identity.organisation,
    identity.email,
    profile.rooms.length
      ? `${profile.rooms.length} ${messagesLocale[profile.rooms.length === 1 ? 'DATA_ROOM' : 'DATA_ROOMS']}`
      : null,
  ].filter((detail): detail is string => Boolean(detail));
  // Only with a room selected: across rooms there is no single grant to report.
  const status = dataRoomId && profile.accessStatus && ACCESS_STATUS[profile.accessStatus];

  return (
    <div className="flex items-center justify-between gap-4">
      <div className="flex min-w-0 items-center gap-3">
        <AccessAvatar
          className="size-12"
          label={identity.name}
          labelClassName="text-[19.2px] leading-[28.8px]"
        />
        <div className="flex min-w-0 flex-col">
          <h2 className="truncate text-xl font-semibold text-[#181D27]">{identity.name}</h2>
          {status && (
            <span className={twJoin('text-label-md font-medium', status.className)}>
              <AppFormattedMessage id={status.label} />
            </span>
          )}
        </div>
      </div>
      <div className="flex min-w-0 items-center gap-3 text-label-sm font-medium text-gray-500">
        {details.map((detail, index) => (
          <Fragment key={index}>
            {index > 0 && <span className="h-3 w-px shrink-0 bg-gray-200" />}
            <span className="truncate">{detail}</span>
          </Fragment>
        ))}
      </div>
      {/* §6: sourced from the grants, so a revoked one is still selectable and still says so. */}
      <FilterSelect
        allLabel="ALL_DATA_ROOMS"
        onChange={onScope}
        options={profile.rooms.map((room) => ({
          label:
            room.accessStatus === DataRoomAccessStatus.REVOKED
              ? `${room.name} (${messagesLocale['REVOKED']})`
              : room.name,
          value: room.dataRoomId,
        }))}
        value={dataRoomId}
      />
    </div>
  );
};

/**
 * One visitor's profile — 20.1.4 §5 to §9.
 *
 * Reached by an opaque key rather than by an email address, which is the whole reason the key
 * exists: a path segment lands in access logs, in Sentry breadcrumbs and in the browser's history,
 * and a data room's audience does not belong in any of them. The key is derived from the identity
 * snapshot the journal keeps, so this page still resolves after the grant it came from is deleted.
 *
 * §6 and §7 disagree about what the cards count — the visitor across every room, or the selected
 * room — and one row of cards cannot be both. They follow the selector: "All Data Rooms" is §6 and
 * a specific room is §7. Fixed totals beside a selector that moved only the timeline would put two
 * scopes on one screen with nothing to tell them apart.
 */
export const ActivityVisitorDetailTab: FC = () => {
  const navigate = useNavigate();
  const { visitorKey = '' } = useParams<{ visitorKey: string }>();
  const { toolbar } = useOutletContext<ActivityOutletContext>();
  const [window, setWindow] = useState<DataRoomActivityWindow>(DEFAULT_DATA_ROOM_ACTIVITY_WINDOW);
  const [dataRoomId, setDataRoomId] = useState('');
  const { profile, isError, isPending, isReloading, retry, timeline } = useActivityVisitor({
    dataRoomId: dataRoomId || undefined,
    visitorKey,
    window,
  });
  // The timeline stands for the window and room its data is for: while another one loads the old
  // days stay, dimmed, and once it lands the list is a new one, easing to its height.
  const timelineKey = useRef('');

  if (!isReloading) timelineKey.current = `${window}|${dataRoomId}`;

  const renderContent = () => {
    if (isPending) {
      return <ActivityVisitorPlaceholder />;
    }

    if (isError || !profile) {
      return <ActivityLoadError onRetry={() => void retry()} />;
    }

    const { totals } = profile;
    const accessibleRooms = profile.rooms.filter(
      ({ accessStatus }) => accessStatus === DataRoomAccessStatus.ACTIVE,
    ).length;

    return (
      <div className="p-4">
        {/* Header and timeline rise in on arrival; a new scope or window dims them while it loads. */}
        <StaggerFadeIn
          className={twJoin(
            'flex flex-col gap-4 rounded-lg bg-white p-4 shadow-sm transition-opacity duration-200',
            isReloading && 'opacity-60',
          )}
        >
          <StaggerItem>
            {/* §5, and §7 scoped to whatever the selector says. */}
            <MetricPanel
              header={
                <VisitorHeader dataRoomId={dataRoomId} onScope={setDataRoomId} profile={profile} />
              }
            >
              {/* The grants as they stand, whatever room or window is picked: how many rooms
                  this visitor can open today. */}
              <MetricCard
                icon={<MetricIcon src={activeDataRoomsIcon} />}
                label="DATA_ROOMS_WITH_ACCESS"
                parts={[{ value: accessibleRooms }]}
              />
              <MetricCard
                icon={<MetricIcon src={roomTotalViewsIcon} />}
                label="TOTAL_VIEWS"
                parts={[{ value: totals.views }]}
              />
              <MetricCard
                icon={<MetricIcon src={roomTotalDownloadsIcon} />}
                label="TOTAL_DOWNLOADS"
                parts={[{ value: totals.downloads }]}
              />
              <MetricCard
                icon={<MetricIcon src={totalViewingTimeIcon} />}
                label="TOTAL_VIEWING_TIME"
                parts={durationParts(totals.viewingTimeMs)}
              />
              {/* The design's fourth card is an average session, which the profile does not carry;
                when they were last active is the one figure here it does. */}
              <MetricCard
                icon={<MetricIcon src={activityTimelineIcon} />}
                label="LAST_ACTIVE"
                value={<LastActive at={totals.lastActiveAt} />}
              />
            </MetricPanel>
          </StaggerItem>

          {/* §8 */}
          <StaggerItem className="rounded-md bg-white px-4 pb-4 shadow-sm">
            <AnimatedHeight animationKey={timelineKey.current}>
              <ActivityTimeline
                days={profile.timeline}
                emptyLabel={
                  dataRoomId ? 'NO_ACTIVITY_IN_THIS_DATA_ROOM' : 'NO_ACTIVITY_IN_THIS_PERIOD'
                }
                isReloading={isReloading}
                key={timelineKey.current}
                timeline={timeline}
              />
            </AnimatedHeight>
          </StaggerItem>
        </StaggerFadeIn>
      </div>
    );
  };

  return (
    <div className="flex h-full min-h-0 w-full flex-1 flex-col overflow-y-auto">
      {/* Drawn beside the sub-tab buttons, but the window it sets stays this screen's own. */}
      {toolbar &&
        createPortal(<ActivityWindowToggle onChange={setWindow} value={window} />, toolbar)}
      <div className="px-4 pt-4">
        <button
          className="flex items-center gap-2 text-sm font-medium leading-4 text-gray-700"
          onClick={() => navigate(PageRoute.DATA_ROOM_ACTIVITY_VISITORS)}
          type="button"
        >
          <ChevronLeftIcon className="size-4" />
          <AppFormattedMessage id="ALL_VISITORS" />
        </button>
      </div>
      {renderContent()}
    </div>
  );
};
